import "../../assets/css/results/map.css"

import Axios from "axios"
import mapboxgl from 'mapbox-gl';
import { useState, useRef, useEffect, useMemo } from "react"
import ReactMapGL, { Marker, Layer, Source } from "react-map-gl"

import CustomPopup from "./customPopup.jsx"
import markerLight from "../../assets/images/markerLightMode.svg"
import markerLightActive from "../../assets/images/markerLightActive.svg"
import nonInteractable from "../../assets/images/nonInteractableMarker.svg"
import arrow from "../../assets/images/arrow.svg"
import { LAYER_STYLE, MEXICO_CENTER, calculateCenter, calculateZoom } from "./utils.js"
import { useAuth } from "../../context/auth-context.jsx";
import { useGeneralStates } from "../../context/general-context.jsx";
import { ACTIONS, RESULTS_SECTIONS } from "../../utils/globalUtils.js";
import StreetviewMap from "./streetView.jsx"

mapboxgl.accessToken = 'pk.eyJ1Ijoid2Vic2hhdSIsImEiOiJjampmd2E2dzk1M21lM3FtbWRibzZ1dzdyIn0.Uy8XcDO3YrXX6OY5fFY5JA'

export default function Map({ setNavigationVisibility, citiesUrlPhotos }) {
    const mapRef = useRef(null)

    const { user } = useAuth()
    const { state, dispatch } = useGeneralStates()

    //data for the source
    const [routeGeoJSON, setRouteGeoJSON] = useState({
        type: 'FeatureCollection',
        features: [
            { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } }
        ]
    })

    const [activePopup, setActivePopup] = useState(null)
    const [routeInProgress, setRouteInProgress] = useState(false)
    const [route, setRoute] = useState(null)
    const [distance, setDistance] = useState(null)
    const [activeStreetview, setActiveStreetview] = useState(null)

    useEffect(() => {
        setActivePopup(null)
    }, [state.resultsSection])

    // #region handle functions
    //close popup when clicking in the map or another marker
    function handleClosePopup(event) {
        const { className } = event.target
        if (className === "mapboxgl-canvas" || className === "marker-popup") {
            closePopUps()
        }
    }

    function handleShowPopup(id, lngLat) {
        setActivePopup(prev => {
            if (prev === id) return null

            return id
        })

        lngLat && flyToDestination(lngLat)
    }

    function closePopUps() {
        setActivePopup(null)
    }

    function flyToDestination(lngLat, zoom = 7) {
        const map = mapRef.current

        if (!map) return

        map.flyTo({
            center: lngLat,
            essential: true,
            curve: 3,
            speed: 0.2,
            pitch: 1,
            zoom: zoom,
            duration: 1500,
        })
    }

    function handleCreateRoute(citySelected) {
        setRouteGeoJSON({
            type: 'FeatureCollection',
            features: [
                { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } }
            ]
        })
        setRouteInProgress(true)

        const inboundLocation = [state.supplierLocation.longitude, state.supplierLocation.latitude]
        const origin = citySelected.coordenates
        const destination = [state.clientLocation.longitude, state.clientLocation.latitude]

        const center = calculateCenter(inboundLocation, destination)
        const zoom = calculateZoom(inboundLocation, destination)

        closePopUps()
        flyToDestination(center, zoom)

        getNewRoute(inboundLocation, origin, destination)
    }

    async function getNewRoute(inboundLocation, origin, destination) {
        try {
            const { data } = await Axios.get(`https://api.mapbox.com/directions/v5/mapbox/driving/${inboundLocation[0]},${inboundLocation[1]};${origin[0]},${origin[1]};${destination[0]},${destination[1]}?overview=full&geometries=geojson&access_token=${mapboxgl.accessToken}`)

            const route = data.routes[0].geometry.coordinates

            animateRoute(route)

            setDistance({
                km: Math.ceil((data.routes[0].distance) / 1000),
                miles: Math.ceil((data.routes[0].distance) * 0.000621371)
            })

            setRoute(data.routes[0].geometry.coordinates)
        } catch (error) {
            console.log("Error while trying to get the route")
        }
    }

    function animateRoute(route) {
        if (!route) return

        for (let index = 0; index < route.length; index++) {
            setTimeout(() => {
                setRouteGeoJSON((prevRouteGeoJSON) => {
                    const newCoordinates = [...prevRouteGeoJSON.features[0].geometry.coordinates];
                    newCoordinates.push(route[index]);
                    return {
                        ...prevRouteGeoJSON,
                        features: [
                            {
                                ...prevRouteGeoJSON.features[0],
                                geometry: {
                                    ...prevRouteGeoJSON.features[0].geometry,
                                    coordinates: newCoordinates,
                                },
                            },
                        ],
                    };
                });
                
                if (index + 1 === route.length) setRouteInProgress(false)
            }, (30 / route.length) * index);
        }
    }

    function animateColumns () {
        const map = mapRef.current

        dispatch({ type: ACTIONS.setShowColumn, payload: "show animate"})

        map.once("moveend", () => {
            setNavigationVisibility("")
        })
        
        setTimeout(() => {
            map.flyTo({
                center: MEXICO_CENTER,
                essential: true,
                curve: 1.5,
                speed: 1.3,
                pitch: 1,
                zoom: 5,
                duration: 15000,
            })
        }, 700)
    }

    function handleStreetView(id) {
        setActivePopup(null)
        setActiveStreetview(id)
    }


    const headquarters = useMemo(() => {
        if (!user) return []

        return user?.inputsData?.headquarters.center
    }, [user])
    const mapSectionClass = `mapSection ${state.resultsSection !== RESULTS_SECTIONS.map ? "move" : ""}`

    // #region JSX
    return (
        <section className={mapSectionClass} onClick={handleClosePopup}>
            {user && 
                <ReactMapGL
                    ref={mapRef}
                    //? This is for map appear just in mexico
                    // initialViewState={{
                    //     longitude: MEXICO_CENTER[0],
                    //     latitude: MEXICO_CENTER[1],
                    //     zoom: 5,
                    //     pitch: 1,
                    // }}
                    initialViewState={{
                        longitude: headquarters[0],
                        latitude: headquarters[1],
                        zoom: 11,
                        pitch: 60,
                    }}
                    style={{ width: "100dvw", height: "100dvh" }}
                    mapStyle="mapbox://styles/webshau/clka2yg1r004g01rm2a9va4h4"
                    mapboxApiAccessToken={mapboxgl.accessToken}
                    projection={"globe"}
                    onLoad={animateColumns}
                >
                    {user?.selectedCities?.map(city => {
                        return <Marker
                            key={city._id}
                            longitude={city.coordenates[0]}
                            latitude={city.coordenates[1]}
                            anchor="bottom"
                            style={city._id === activePopup ? { zIndex: "7" } : { zIndex: "1" }}
                        >
                            <div className="marker-popup" onClick={() => handleShowPopup(city._id, city.coordenates)}>
                                <img
                                    className={`marker ${city._id === activePopup ? "active" : ""}`}
                                    alt="Prodensa Teaser"
                                    src={city._id === activePopup ? markerLightActive : markerLight}
                                />
                                <CustomPopup
                                    cityId={city._id}
                                    activePopup={activePopup}
                                    closePopUps={closePopUps}
                                    routeInProgress={routeInProgress}
                                    handleCreateRoute={handleCreateRoute}
                                    handleStreetView={handleStreetView}
                                    citiesUrlPhotos={citiesUrlPhotos}
                                />
                                <div className={`cityTitle ${activePopup !== null ? "hide" : ""}`}>
                                    <h5>{city.municipality_name}</h5>
                                </div>
                            </div>
                        </Marker>

                    })}

                    <Marker
                        longitude={state.clientLocation.longitude}
                        latitude={state.clientLocation.latitude}
                        anchor="bottom"
                        style={{ zIndex: "6" }}
                    >
                        <div className="placeMarker outbound" onClick={() => handleShowPopup(state.clientLocation.name)}>
                            <img src={arrow} />
                            <div className={`placeTitleContainer ${activePopup === state.clientLocation.name ? "show" : ""}`} >
                                <span className="placeTitle">{state.clientLocation.name}</span>
                            </div>
                        </div>
                    </Marker>

                    <Marker
                        longitude={state.supplierLocation.longitude}
                        latitude={state.supplierLocation.latitude}
                        anchor="bottom"
                        style={{ zIndex: "6" }}
                    >
                        <div className="placeMarker" onClick={() => handleShowPopup(state.supplierLocation.name)}>
                            <img src={arrow} />
                            <div className={`placeTitleContainer ${activePopup === state.supplierLocation.name ? "show" : ""}`} >
                                <span className="placeTitle">{state.supplierLocation.name}</span>
                            </div>
                        </div>
                    </Marker>

                    {route && <Marker
                        longitude={state.clientLocation.longitude}
                        latitude={state.clientLocation.latitude - 0.7}
                        anchor="bottom"
                    >
                        <div className="distance">
                            <span>{distance.km} Km / {distance.miles} Miles</span>
                        </div>
                    </Marker> }

                    <Source id="my-data" type="geojson" data={routeGeoJSON} >
                        <Layer id="my-layer" {...LAYER_STYLE} />
                    </Source>

                    <StreetviewMap 
                        activeStreetview={activeStreetview} 
                        setActiveStreetview={setActiveStreetview} 
                    />
                </ReactMapGL>
            }
        </section>
    )
}